import { Component, OnChanges, OnDestroy, SimpleChanges, Optional } from '@angular/core';
import { Input } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, ControlContainer, FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

import { deviceService } from '@unpispas/upp-base';
import { WSConnection } from '@unpispas/upp-base';
import { WSInput } from '@unpispas/upp-base';

interface ErrorInfo {
    [key: string]: string;
}

/**
 * Minimal scale identity for WS START/STOP (model + optional serial port).
 * Kept free of upp-data so this widget stays UI-only.
 */
export interface ScaleInputDevice {
    model: string | null;
    port?: string | null;
}

/**
 * Weight field backed by `upp-input`, with optional live capture from a scale
 * over an already-open `WSConnection`.
 *
 * Place under a reactive `[formGroup]`. Pass `controlName` of the weight
 * control; when `connection` and `device` are set, starts capture on init and
 * ends it on destroy (or when inputs clear). Caller owns `WSOpen` / `WSClose`.
 *
 * @selector `upp-scale-input`
 */
@Component({
    selector: 'upp-scale-input',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-input.html',
    styleUrls: [
        './upp-input.scss'
    ],
})
export class UppScaleInputComponent implements OnChanges, OnDestroy {

    /********************************/
    /* INPUTS                       */
    /********************************/

    @Input() controlName = 'weight';
    @Input() placeholder = '';
    @Input() title = '';
    @Input() type = 'number';
    @Input() kiosk = true;
    @Input() readonly = false;
    @Input() disabled = false;
    @Input() errornfo: ErrorInfo = {};

    @Input() connection: WSConnection | null = null;
    @Input() device: ScaleInputDevice | null = null;

    /********************************/
    /* STATE                        */
    /********************************/

    private _scale: WSInput | null = null;
    private _scaleconnected = false;
    private _scaleconnecting = false;
    private _connectingTimer: ReturnType<typeof setTimeout> | null = null;
    private _scale_lastvalue: string | null = null;
    private _scale_read_subscription: Subscription | null = null;
    private _scale_lost_subscription: Subscription | null = null;

    constructor(
        @Optional() private controlContainer: ControlContainer,
        private devices: deviceService,
        private change: ChangeDetectorRef,
    ){
        // nothing to do
    }

    /********************************/
    /* UI GETTERS                   */
    /********************************/

    get ParentForm(): FormGroup | null {
        const _group = this.controlContainer?.control;
        return (_group instanceof FormGroup) ? _group : null;
    }

    get WeightControl(): FormControl | null {
        if (!this.ParentForm || !this.controlName){
            return null;
        }

        const _control: AbstractControl | null = this.ParentForm.get(this.controlName);
        return (_control instanceof FormControl) ? _control : null;
    }

    get CanScale(): boolean {
        return !!(this.connection && this.device?.model);
    }

    get ScaleConnected(): boolean {
        return this._scaleconnected;
    }

    /********************************/
    /* LIFECYCLE                    */
    /********************************/

    ngOnChanges(changes: SimpleChanges): void {
        // Prefer FormControl.disable/enable over template [disabled] with formControlName.
        if (changes['disabled'] || changes['controlName']){
            if (this.disabled){
                this.WeightControl?.disable({ emitEvent: false });
            }
            else {
                this.WeightControl?.enable({ emitEvent: false });
            }
        }

        if (!this.CanScale){
            void this._StopCapture();
            return;
        }

        const _transportChanged = !!(changes['connection'] || changes['device']);
        if (_transportChanged && (this._scaleconnected || this._scaleconnecting)){
            void this._RestartCapture();
            return;
        }

        void this._StartCapture();
    }

    ngOnDestroy(): void {
        void this._StopCapture();
    }

    /********************************/
    /* EVENT HANDLERS               */
    /********************************/

    /**
     * Toggles live scale capture when a connection and device are configured.
     */
    OnScaleConnect(): void {
        if (!this.CanScale){
            return;
        }

        if (this._scaleconnected){
            void this._StopCapture();
        }
        else {
            void this._StartCapture();
        }
    }

    /********************************/
    /* PRIVATE                      */
    /********************************/

    private async _RestartCapture(): Promise<void> {
        await this._StopCapture();
        await this._StartCapture();
    }

    private async _StartCapture(): Promise<void> {
        if (!this.CanScale || !this.connection || !this.device){
            return;
        }

        if (this._scaleconnecting || this._scaleconnected){
            return;
        }

        this._ClearConnectingTimer();
        this._scaleconnecting = true;
        this._connectingTimer = setTimeout(() => {
            this._connectingTimer = null;
            this._scaleconnecting = false;
        }, 1000);

        this._scale = this.devices.WSInputScale(this.connection, this.device);
        if (!this._scale){
            this._ClearConnectingTimer();
            this._scaleconnected = false;
            this.change.markForCheck();
            return;
        }

        this._scaleconnected = !!(await this._scale.doIniRead({}));
        if (!this._scaleconnected){
            await this._scale.Release();
            this._scale = null;
            this._ClearConnectingTimer();
            this.change.markForCheck();
            return;
        }

        this._scale_read_subscription?.unsubscribe();
        this._scale_lost_subscription?.unsubscribe();

        this._scale_read_subscription = this._scale.OnInputRead.subscribe(
        data => {
            if (this._scale_lastvalue == data){
                return;
            }

            this._scale_lastvalue = data;
            const _weight = Number(data);
            const _control = this.WeightControl;
            if (_control){
                _control.setValue(Number.isFinite(_weight) ? _weight : data);
                _control.markAsDirty();
            }
            this.change.markForCheck();
        });

        this._scale_lost_subscription = this._scale.OnInputLost.subscribe(
        () => {
            void this._StopCapture();
        });

        this.change.markForCheck();
    }

    private async _StopCapture(): Promise<void> {
        this._ClearConnectingTimer();
        
        this._scaleconnected = false;
        this._scaleconnecting = false;
        this._scale_lastvalue = null;

        this._scale_read_subscription?.unsubscribe();
        this._scale_read_subscription = null;

        this._scale_lost_subscription?.unsubscribe();
        this._scale_lost_subscription = null;

        if (this._scale){
            await this._scale.Release();
            this._scale = null;
        }

        this.change.markForCheck();
    }

    private _ClearConnectingTimer(): void {
        if (this._connectingTimer == null){
            return;
        }

        clearTimeout(this._connectingTimer);
        this._connectingTimer = null;
        this._scaleconnecting = false;
    }
}
